/**
 * Вейвер в отчёте обязателен (01.10.2026, владелец): «поле вейвера заполняется в любом случае:
 * если вейвера нет — оператор ставит галочку и пишет, почему; типичные причины, в том числе своя».
 *
 * Блок «Вейвер» отчёта: фото (WaiverAttach — уходят сразу при выборе) + «Вейвера нет» с причиной.
 * Обязателен только основному отчёту игры с даты запуска (@ub/shared/waiver) — сервер проверяет
 * то же самое (game-reports/waiver-gate.ts). Отметка живёт в черновике отдельным ключом, а при
 * возврате на исправление подтягивается из своего отчёта.
 */
import { useEffect, useRef, useState } from "react";
import { NO_WAIVER_REASONS, noWaiverReason, waiverRequired } from "@ub/shared/waiver";
import { api } from "../../api/client";
import { ApiException } from "../../api/types";
import { WaiverAttach } from "./waiverAttach";
import m from "./opReport.module.css";
import w from "./waiverBlock.module.css";

export { waiverRequired };

interface Saved { on: boolean; picked: string[]; custom: string }
const key = (b: number, o: string) => `ub_nwdraft_${b}_${o}`;
function load(b: number, o: string): Saved | null {
  try { const v = JSON.parse(localStorage.getItem(key(b, o)) ?? "null") as Saved | null; return v && typeof v.on === "boolean" ? v : null; } catch { return null; }
}

/** Причина из базы → кнопки + своя (для возврата на исправление). */
function split(reason: string): { picked: string[]; custom: string } {
  let rest = reason;
  const picked = NO_WAIVER_REASONS.filter((r) => { if (!rest.includes(r)) return false; rest = rest.replace(r, ""); return true; });
  return { picked, custom: rest.replace(/^[\s.]+|[\s.]+$/g, "") };
}

export function useNoWaiver(bookingId: number, operatorId: string, mode?: string) {
  const [on, setOn] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [custom, setCustom] = useState("");
  const [uploading, setUploading] = useState(0);
  const [invalid, setInvalid] = useState(false);
  /* в исправлении — роль и дата СВОЕГО отчёта (как у сервера: prev.is_primary, prev.date) */
  const [ownPrimary, setOwnPrimary] = useState<boolean | null>(null);
  const [ownDate, setOwnDate] = useState<string | null>(null);
  /* сервер ответил waiver_required, хотя форма считала вейвер ненужным/фото есть — показываем блок */
  const [forced, setForced] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const ready = useRef(false);

  useEffect(() => {
    if (mode === "fix" || mode === "edit") {
      api<{ results?: Record<string, unknown>[] }>(`/api/game-reports?booking_id=${bookingId}`)
        .then((r) => {
          const own = (r.results ?? []).find((x) => String(x.operator_id) === String(operatorId) && !x.archived);
          if (own) { setOwnPrimary(Number(own.is_primary) === 1); if (own.date) setOwnDate(String(own.date)); }
          if (own && Number(own.no_waiver)) { const s = split(String(own.no_waiver_reason ?? "")); setOn(true); setPicked(s.picked); setCustom(s.custom); }
        }).catch(() => { /* без подсказки — оператор отметит заново */ }).finally(() => { ready.current = true; });
      return;
    }
    const d = load(bookingId, operatorId);
    if (d) { setOn(d.on); setPicked(d.picked ?? []); setCustom(d.custom ?? ""); }
    ready.current = true;
  }, [bookingId, operatorId, mode]);

  useEffect(() => {
    if (!ready.current) return;
    try { localStorage.setItem(key(bookingId, operatorId), JSON.stringify({ on, picked, custom })); } catch { /* приватный режим — без черновика */ }
  }, [bookingId, operatorId, on, picked, custom]);

  const reason = noWaiverReason(picked, custom);
  return {
    on, picked, custom, uploading, invalid, ref, reason,
    setUploading,
    toggle: () => { setOn((v) => !v); setInvalid(false); },
    pick: (r: string) => { setPicked((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r])); setInvalid(false); },
    setCustom: (v: string) => { setCustom(v); if (v.trim()) setInvalid(false); },
    /** Нужен ли вейвер: основной отчёт (в исправлении — роль своего отчёта) и дата игры с запуска. */
    isRequired: (full: boolean, gameDate: string) => forced || waiverRequired(ownDate ?? gameDate, ownPrimary ?? full),
    /** Текст ошибки для отправки или null. hasPhoto — фото по последнему списку формы; refresh —
     *  перечитать список у формы (коллега мог приложить фото, пока отчёт был открыт). */
    async check(hasPhoto: boolean, required: boolean, refresh?: () => unknown): Promise<string | null> {
      if (!required || hasPhoto || (on && reason)) return null;
      if (uploading > 0) return "Фото вейвера ещё загружается — подожди пару секунд.";
      const fresh = await api<{ waiver_files?: unknown[] }>(`/api/game-reports/players?booking_id=${bookingId}`)
        .then((r) => (r.waiver_files ?? []).length).catch(() => 0);
      if (fresh > 0) { void refresh?.(); return null; }
      if (!on) { setInvalid(true); return "Вейвер обязателен: приложи фото или отметь «Вейвера нет» и укажи причину."; }
      setInvalid(true);
      return "Укажи, почему нет вейвера: выбери причину или впиши свою.";
    },
    /** Сервер отказал из-за вейвера (фото удалили, пока форма открыта) — показать блок и обновить фото. */
    onServerError(e: unknown, refresh?: () => unknown) {
      if (!(e instanceof ApiException) || (e.body as { code?: string } | null)?.code !== "waiver_required") return;
      setForced(true); setInvalid(true); void refresh?.();
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
    },
    /** Поля отчёта: фото есть — отметки нет (сервер всё равно её сбросит). */
    payload: (hasPhoto: boolean) => ({ no_waiver: on && !hasPhoto ? 1 : 0, no_waiver_reason: on && !hasPhoto ? reason : "" }),
    clear: () => { try { localStorage.removeItem(key(bookingId, operatorId)); } catch { /* нечего чистить */ } },
  };
}
export type NoWaiver = ReturnType<typeof useNoWaiver>;

export function WaiverBlock({ nw, bookingId, attachedBy, existing, onUploaded, required }: {
  nw: NoWaiver; bookingId: number; attachedBy?: string | null;
  existing: { id: number; name: string; editable?: boolean }[]; onUploaded: () => void; required: boolean;
}) {
  const hasPhoto = existing.length > 0;
  return (
    <div ref={nw.ref} className={`${w.box} ${nw.invalid ? w.bad : ""}`}>
      <WaiverAttach bookingId={bookingId} attachedBy={attachedBy} existing={existing} onUploaded={onUploaded} onBusy={nw.setUploading} />
      {required && !hasPhoto ? (<>
        <button type="button" className={`${w.check} ${nw.on ? w.on : ""}`} aria-pressed={nw.on} onClick={nw.toggle}>
          <span className={w.box2} aria-hidden="true">{nw.on ? "✓" : ""}</span>Вейвера нет
        </button>
        {nw.on ? (<>
          <div className={m.lbl}>Почему нет вейвера<span className={m.req}>*</span></div>
          <div className={m.tags}>
            {NO_WAIVER_REASONS.map((r) => (
              <button key={r} type="button" className={`${m.tag} ${nw.picked.includes(r) ? m.on : ""}`} aria-pressed={nw.picked.includes(r)} onClick={() => nw.pick(r)}>{r}</button>
            ))}
          </div>
          <textarea className={m.ta} value={nw.custom} placeholder="Или впиши свою причину…" style={{ minHeight: 44, marginTop: 6 }}
            onChange={(e) => nw.setCustom(e.target.value)} />
        </>) : <div className={w.hint}>Вейвер обязателен: приложи фото или отметь, что его нет.</div>}
      </>) : null}
    </div>
  );
}
